import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { toast } from 'sonner'
import { createTransaction } from '@/api/transactions'
import { isTransactable, type AccountSummary } from '@/api/accounts'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { track } from '@/lib/analytics'
import { ROUTES } from '@/routes/routes'

/** "Someone else paid" in the Paid-from list — not an account, so never sent as one. */
export const PAID_BY_PERSON = '__person__'

export const incomeExpenseSchema = z.object({
  // "Someone else paid" is the non-empty PAID_BY_PERSON sentinel, so it passes here.
  financialAccountId: z.string().min(1, 'Account is required'),
  amount: z.coerce.number().positive('Amount must be positive'),
  date: z.string().min(1, 'Date is required'),
  description: z.string().min(1, 'Description is required').max(100),
  categoryId: z.string().min(1, 'Category is required'),
  notes: z.string().optional(),
  attributedToHouseholdMemberId: z.string().optional(),
  isCapitalEvent: z.boolean().default(false),
  capitalEventLabel: z.string().max(100).optional(),
  // INCOME only — an optional link to the transaction this compensates for.
  relatedTransactionId: z.string().optional(),
})
export type IncomeExpenseForm = z.infer<typeof incomeExpenseSchema>

// Paying only for other people is lending, not spend, so it has no category.
export const paidForOthersSchema = incomeExpenseSchema.extend({ categoryId: z.string().optional() })

// Paid from several accounts: the parts carry the accounts, so there is no single one to require.
export const multiAccountSchema = incomeExpenseSchema.extend({
  financialAccountId: z.string().optional(),
})

export const refundSchema = z
  .object({
    // Standalone = the original expense isn't tracked in the app; pick an account + category instead.
    standalone: z.boolean(),
    relatedTransactionId: z.string().optional(),
    financialAccountId: z.string().optional(),
    categoryId: z.string().optional(),
    amount: z.coerce.number().positive('Amount must be positive'),
    date: z.string().min(1, 'Date is required'),
    description: z.string().min(1, 'Description is required').max(100),
    notes: z.string().optional(),
  })
  .refine((v) => v.standalone || !!v.relatedTransactionId, {
    message: 'Select the expense being refunded',
    path: ['relatedTransactionId'],
  })
  .refine((v) => !v.standalone || !!v.financialAccountId, {
    message: 'Account is required',
    path: ['financialAccountId'],
  })
  .refine((v) => !v.standalone || !!v.categoryId, {
    message: 'Category is required',
    path: ['categoryId'],
  })
export type RefundForm = z.infer<typeof refundSchema>

export const transferSchema = z
  .object({
    fromAccountId: z.string().min(1, 'Source account is required'),
    toAccountId: z.string().min(1, 'Destination account is required'),
    amount: z.coerce.number().positive('Amount must be positive'),
    date: z.string().min(1, 'Date is required'),
    description: z.string().min(1, 'Description is required').max(100),
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, {
    message: 'Pick two different accounts',
    path: ['toAccountId'],
  })
export type TransferForm = z.infer<typeof transferSchema>

export const editTransferSchema = z.object({
  amount: z.coerce.number().positive('Amount must be positive'),
  date: z.string().min(1, 'Date is required'),
  description: z.string().min(1, 'Description is required').max(100),
})
export type EditTransferForm = z.infer<typeof editTransferSchema>

/**
 * Loans are excluded: they change balance only through their own repayment flow. Credit cards
 * have no such flow — a generic transaction is how a purchase or payment on one is recorded.
 */
export function transactableAccounts(accounts: AccountSummary[]): AccountSummary[] {
  return accounts.filter((a) => isTransactable(a.type))
}

/** A card's balance is stored negative while money is owed on it. */
export function cardOutstanding(card: AccountSummary): number {
  return card.balance < 0 ? -card.balance : 0
}

export function errorText(error: unknown): string {
  return (
    (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
    (error as Error)?.message ??
    'Something went wrong. Please try again.'
  )
}

/** Toast when a payment eats into money earmarked for a goal, with a link to that goal. */
export function useEarmarkWarning() {
  const { format } = useFormatCurrency()
  const navigate = useNavigate()
  return (
    warning: { shortfall: number; goals: { id: string; name: string }[] } | null | undefined
  ) => {
    if (!warning) return
    const goal = warning.goals[0]
    toast.warning(`${goal.name} is now short by ${format(warning.shortfall)}`, {
      action: { label: 'View goal', onClick: () => navigate(ROUTES.APP.GOAL_DETAIL(goal.id)) },
    })
  }
}

/** Query keys a new transaction can change, on whichever page the add flow was opened from. */
export const AFTER_SAVE_KEYS = [
  'transactions',
  'accounts',
  'dashboard',
  'budget',
  'analytics',
  'onboarding',
  'contacts',
  'owed-summary',
]

export function useCreateTransaction(onDone: () => void) {
  const queryClient = useQueryClient()
  const warnEarmark = useEarmarkWarning()
  return useMutation({
    mutationFn: (payload: Parameters<typeof createTransaction>[0]) => createTransaction(payload),
    onSuccess: (data, payload) => {
      track('transaction_added', { type: payload.type })
      for (const key of AFTER_SAVE_KEYS) queryClient.invalidateQueries({ queryKey: [key] })
      warnEarmark(data?.earmarkWarning)
      onDone()
    },
  })
}
